import type { components } from '@trackercop/contracts';

// Centralised TanStack Query key factory. Never inline `['foo', id]`
// arrays inside components — the factory is the only way a key is
// constructed, so cache invalidation and prefetching stay coherent as
// new filters are added. Each sub-tree starts with a unique scope name
// so `queryClient.invalidateQueries({ queryKey: keys.sites.all })`
// sweeps exactly what the caller intended.

export type ConsentModeFilter = components['schemas']['ConsentMode'] | undefined;
export type ScriptCategoryFilter = components['schemas']['ScriptCategory'] | undefined;
export type CookieCategoryFilter = components['schemas']['CookieCategory'] | undefined;
export type PayloadCategoryFilter = components['schemas']['PayloadCategory'] | undefined;
export type ClassificationSourceFilter =
  | components['schemas']['ClassificationSource']
  | undefined;
export type CmpProviderFilter = components['schemas']['CmpProvider'] | undefined;

export type EventLevelFilter = components['schemas']['EventLevel'] | undefined;
export type EventSourceFilter = components['schemas']['EventSource'] | undefined;

export interface AdminMetricsQueryFilters {
  metric: string;
  from: string;
  to: string;
}

export interface AdminEventsQueryFilters {
  level?: EventLevelFilter;
  source?: EventSourceFilter;
  limit?: number;
  cursor?: string;
}

export interface ScriptsQueryFilters {
  page?: number;
  pageSize?: number;
  domain?: string;
  category?: ScriptCategoryFilter;
  consentMode?: ConsentModeFilter;
  gtmContainerId?: string;
  cmpProvider?: CmpProviderFilter;
  classificationSource?: ClassificationSourceFilter;
}

export interface CookiesQueryFilters {
  page?: number;
  pageSize?: number;
  domain?: string;
  category?: CookieCategoryFilter;
  consentMode?: ConsentModeFilter;
  gtmContainerId?: string;
}

export interface PayloadsQueryFilters {
  page?: number;
  pageSize?: number;
  destinationDomain?: string;
  category?: PayloadCategoryFilter;
  consentMode?: ConsentModeFilter;
  scriptId?: string;
  gtmContainerId?: string;
}

export const queryKeys = {
  me: ['me'] as const,
  sites: {
    all: ['sites'] as const,
    list: (page?: number, pageSize?: number) => ['sites', 'list', page ?? 1, pageSize ?? 50] as const,
    detail: (siteId: string) => ['sites', 'detail', siteId] as const,
    scanUrls: (siteId: string) => ['sites', 'scanUrls', siteId] as const,
    crawls: (siteId: string, page?: number) => ['sites', siteId, 'crawls', page ?? 1] as const,
    baseline: (siteId: string) => ['sites', siteId, 'baseline'] as const,
    drift: (siteId: string) => ['sites', siteId, 'drift'] as const,
  },
  dataFlows: {
    bySite: (
      siteId: string,
      filters: { consentMode?: ConsentModeFilter; pageId?: string } = {},
    ) => ['sites', siteId, 'data-flows', filters] as const,
  },
  crawls: {
    detail: (crawlId: string) => ['crawls', crawlId] as const,
    scripts: (crawlId: string, filters: ScriptsQueryFilters) => ['crawls', crawlId, 'scripts', filters] as const,
    scriptsAll: (crawlId: string, pageSize: number) =>
      ['crawls', crawlId, 'scripts', 'all', pageSize] as const,
    cookies: (crawlId: string, filters: CookiesQueryFilters) => ['crawls', crawlId, 'cookies', filters] as const,
    payloads: (crawlId: string, filters: PayloadsQueryFilters) => ['crawls', crawlId, 'payloads', filters] as const,
    payloadsAll: (crawlId: string, pageSize: number) =>
      ['crawls', crawlId, 'payloads', 'all', pageSize] as const,
    payloadsByDestination: (crawlId: string) => ['crawls', crawlId, 'payloads', 'by-destination'] as const,
    payloadsByScript: (crawlId: string) => ['crawls', crawlId, 'payloads', 'by-script'] as const,
    gtmContainers: (crawlId: string) => ['crawls', crawlId, 'gtm', 'containers'] as const,
    gtmTags: (crawlId: string) => ['crawls', crawlId, 'gtm', 'tags'] as const,
    gtmInventory: (crawlId: string, containerId: string) =>
      ['crawls', crawlId, 'gtm', 'inventory', containerId] as const,
    consent: (crawlId: string) => ['crawls', crawlId, 'consent'] as const,
    report: (crawlId: string) => ['crawls', crawlId, 'report'] as const,
    reviews: (crawlId: string) => ['crawls', crawlId, 'reviews'] as const,
  },
  reviews: {
    history: (crawlId: string, entityType: string, entityId: string) =>
      ['reviews', crawlId, entityType, entityId] as const,
  },
  harness: {
    coverage: (crawlId: string) => ['harness', crawlId, 'coverage'] as const,
    crawl: (crawlId: string) => ['harness', crawlId, 'crawl'] as const,
  },
  admin: {
    health: () => ['admin', 'health'] as const,
    metrics: (filters: AdminMetricsQueryFilters) => ['admin', 'metrics', filters] as const,
    queues: () => ['admin', 'queues'] as const,
    events: (filters: AdminEventsQueryFilters) => ['admin', 'events', filters] as const,
    capacity: () => ['admin', 'capacity'] as const,
    dependencies: () => ['admin', 'dependencies'] as const,
    costs: (month?: string) => ['admin', 'costs', month ?? 'current'] as const,
    costHistory: (months: number) => ['admin', 'costs', 'history', months] as const,
  },
} as const;
